/**
 * SurveyBuilder — structured editor for a What's New survey definition.
 *
 * Emits the same SurveyDefinition JSON the raw "Experience data" textarea would
 * (into the tip's experienceData). Enforces stable question ids and, once the
 * survey has responses, locks structural edits (see `locked`).
 *
 * See docs/FeatureDesigns/whats-new-surveys.md.
 */

import { useMemo, useRef, useState } from 'react';
import type {
    SurveyDefinition,
    SurveyQuestion,
    SurveyQuestionType,
    SurveyOption,
} from '@shared-ui/whats-new';
import { IntegerInput } from '@shared-ui/components';
import './SurveyBuilder.css';

const TYPE_OPTIONS: { value: SurveyQuestionType; label: string }[] = [
    { value: 'single', label: 'Single choice' },
    { value: 'multi', label: 'Multiple choice' },
    { value: 'rating', label: 'Rating' },
    { value: 'nps', label: 'NPS (0–10)' },
    { value: 'text', label: 'Short text' },
];

function slug(s: string): string {
    return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
}

/** Parse incoming JSON into a definition, tolerating empty/invalid input. */
function parseInitial(json: string): SurveyDefinition {
    try {
        const obj = JSON.parse(json);
        if (obj && Array.isArray(obj.questions)) {
            return {
                version: typeof obj.version === 'number' ? obj.version : 1,
                questions: obj.questions,
                thanksMessage: typeof obj.thanksMessage === 'string' ? obj.thanksMessage : undefined,
            };
        }
    } catch { /* fall through to empty */ }
    return { version: 1, questions: [] };
}

/** Highest numeric suffix among existing `qN` ids, so new ids never collide. */
function maxIdNum(questions: SurveyQuestion[]): number {
    return questions.reduce((max, q) => {
        const m = /^q(\d+)$/.exec(q.id);
        return m ? Math.max(max, Number(m[1])) : max;
    }, 0);
}

function defaultQuestion(id: string, type: SurveyQuestionType): SurveyQuestion {
    const base: SurveyQuestion = { id, prompt: '', type };
    if (type === 'single' || type === 'multi') base.options = [{ label: '', value: '' }, { label: '', value: '' }];
    if (type === 'rating') base.scale = { min: 1, max: 5 };
    if (type === 'text') base.maxLen = 280;
    return base;
}

interface SurveyBuilderProps {
    value: string;                       // experienceData JSON
    onChange: (json: string) => void;
    /** Structural edits disabled (survey already has responses). */
    locked?: boolean;
}

export function SurveyBuilder({ value, onChange, locked }: SurveyBuilderProps) {
    // Seed once; the parent remounts (key=tip id) when switching tips.
    const initial = useMemo(() => parseInitial(value), []); // eslint-disable-line react-hooks/exhaustive-deps
    const [def, setDef] = useState<SurveyDefinition>(initial);
    const nextId = useRef(maxIdNum(initial.questions) + 1);
    const [showJson, setShowJson] = useState(false);

    const emit = (d: SurveyDefinition) => {
        setDef(d);
        onChange(JSON.stringify(d, null, 2));
    };

    const patchQuestion = (i: number, patch: Partial<SurveyQuestion>) => {
        const questions = def.questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q));
        emit({ ...def, questions });
    };

    const addQuestion = () => {
        const id = `q${nextId.current++}`;
        emit({ ...def, questions: [...def.questions, defaultQuestion(id, 'single')] });
    };

    const removeQuestion = (i: number) => {
        emit({ ...def, questions: def.questions.filter((_, idx) => idx !== i) });
    };

    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= def.questions.length) return;
        const questions = [...def.questions];
        [questions[i], questions[j]] = [questions[j], questions[i]];
        emit({ ...def, questions });
    };

    const changeType = (i: number, type: SurveyQuestionType) => {
        // Reset type-specific config; keep id + prompt + required.
        const q = def.questions[i];
        const fresh = defaultQuestion(q.id, type);
        patchQuestion(i, { type, options: fresh.options, scale: fresh.scale, maxLen: fresh.maxLen });
    };

    const setOption = (qi: number, oi: number, patch: Partial<SurveyOption>) => {
        const q = def.questions[qi];
        const options = (q.options ?? []).map((o, idx) => {
            if (idx !== oi) return o;
            const merged = { ...o, ...patch };
            // Auto-derive value from label while the value hasn't been hand-set.
            if (patch.label !== undefined && (!o.value || o.value === slug(o.label))) {
                merged.value = slug(patch.label);
            }
            return merged;
        });
        patchQuestion(qi, { options });
    };

    const addOption = (qi: number) => {
        const q = def.questions[qi];
        patchQuestion(qi, { options: [...(q.options ?? []), { label: '', value: '' }] });
    };
    const removeOption = (qi: number, oi: number) => {
        const q = def.questions[qi];
        patchQuestion(qi, { options: (q.options ?? []).filter((_, idx) => idx !== oi) });
    };

    return (
        <div className="sb">
            {locked && (
                <p className="sb-lock">
                    🔒 This survey has responses — structural edits are locked so results stay
                    consistent. You can still edit wording. To change the questions, create a new survey.
                </p>
            )}

            {def.questions.map((q, i) => (
                <div className="sb-q" key={q.id}>
                    <div className="sb-q-head">
                        <span className="sb-q-id">{q.id}</span>
                        <select
                            value={q.type}
                            disabled={locked}
                            onChange={e => changeType(i, e.target.value as SurveyQuestionType)}
                        >
                            {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <label className="sb-req">
                            <input
                                type="checkbox"
                                checked={q.required ?? q.type !== 'text'}
                                onChange={e => patchQuestion(i, { required: e.target.checked })}
                            />
                            Required
                        </label>
                        <span className="sb-q-move">
                            <button type="button" disabled={locked || i === 0} onClick={() => move(i, -1)}>↑</button>
                            <button type="button" disabled={locked || i === def.questions.length - 1} onClick={() => move(i, 1)}>↓</button>
                            <button type="button" className="sb-del" disabled={locked} onClick={() => removeQuestion(i)}>✕</button>
                        </span>
                    </div>

                    <input
                        className="sb-prompt"
                        type="text"
                        placeholder="Question prompt"
                        value={q.prompt}
                        onChange={e => patchQuestion(i, { prompt: e.target.value })}
                    />

                    {(q.type === 'single' || q.type === 'multi') && (
                        <div className="sb-options">
                            {(q.options ?? []).map((o, oi) => (
                                <div className="sb-opt" key={oi}>
                                    <input
                                        type="text"
                                        placeholder="Option label"
                                        value={o.label}
                                        onChange={e => setOption(i, oi, { label: e.target.value })}
                                    />
                                    <input
                                        type="text"
                                        className="sb-opt-val"
                                        placeholder="value"
                                        value={o.value}
                                        disabled={locked}
                                        onChange={e => setOption(i, oi, { value: e.target.value })}
                                    />
                                    <button type="button" className="sb-del" disabled={locked} onClick={() => removeOption(i, oi)}>✕</button>
                                </div>
                            ))}
                            {!locked && <button type="button" className="sb-add-opt" onClick={() => addOption(i)}>+ Option</button>}
                        </div>
                    )}

                    {q.type === 'rating' && (
                        <div className="sb-scale">
                            <label>Min <IntegerInput value={q.scale?.min ?? 1} disabled={locked}
                                onChange={v => { if (v != null) patchQuestion(i, { scale: { ...(q.scale ?? { min: 1, max: 5 }), min: v } }); }} /></label>
                            <label>Max <IntegerInput value={q.scale?.max ?? 5} disabled={locked}
                                onChange={v => { if (v != null) patchQuestion(i, { scale: { ...(q.scale ?? { min: 1, max: 5 }), max: v } }); }} /></label>
                            <label>Low label <input type="text" value={q.scale?.minLabel ?? ''}
                                onChange={e => patchQuestion(i, { scale: { ...(q.scale ?? { min: 1, max: 5 }), minLabel: e.target.value } })} /></label>
                            <label>High label <input type="text" value={q.scale?.maxLabel ?? ''}
                                onChange={e => patchQuestion(i, { scale: { ...(q.scale ?? { min: 1, max: 5 }), maxLabel: e.target.value } })} /></label>
                        </div>
                    )}

                    {q.type === 'nps' && <p className="sb-hint">Fixed 0–10 scale.</p>}

                    {q.type === 'text' && (
                        <label className="sb-maxlen">Max length
                            <IntegerInput min={1} value={q.maxLen ?? 280}
                                onChange={v => { if (v != null) patchQuestion(i, { maxLen: v }); }} />
                        </label>
                    )}
                </div>
            ))}

            {!locked && <button type="button" className="sb-add" onClick={addQuestion}>+ Add question</button>}

            <label className="sb-thanks">Thanks message <span className="sb-hint">Shown after submit (optional)</span>
                <input type="text" value={def.thanksMessage ?? ''} placeholder="Thanks for the feedback! 🙌"
                    onChange={e => emit({ ...def, thanksMessage: e.target.value || undefined })} />
            </label>

            <button type="button" className="sb-json-toggle" onClick={() => setShowJson(v => !v)}>
                {showJson ? 'Hide' : 'Show'} JSON
            </button>
            {showJson && (
                <textarea
                    className="sb-json"
                    rows={8}
                    spellCheck={false}
                    value={value}
                    onChange={e => {
                        onChange(e.target.value);
                        // Re-seed the visual builder if the hand-edit parses.
                        try {
                            const obj = JSON.parse(e.target.value);
                            if (obj && Array.isArray(obj.questions)) setDef(parseInitial(e.target.value));
                        } catch { /* leave builder as-is until valid */ }
                    }}
                />
            )}
        </div>
    );
}
